import { DeleteButton, Heading, TextLink } from '@mm/components';
import { DragIcon, PlusCircleIcon, TrashIcon } from '@mm/icons';
import { useViewActions } from '@mm/state/actions/viewActions';
import { PromoterCustomButton } from '@mm/state/model/PromoterModel';
import { StoreState } from '@mm/state/store-interfaces';
import { typedObjectKeys } from '@mm/utils';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Draggable, DropResult } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import Popover from 'react-tiny-popover';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { InputWithEmoji } from '../../../../../../../common/components/InputWithEmoji/InputWithEmoji';
import { MMdndDroppable } from '../../../../../../../common/components/MMdndDroppable/MMdndDroppable';
import { OnClickElsewhere } from '../../../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import { PhoneInput } from '../../../../../../../common/components/PhoneInput/PhoneInput';
import TextInput from '../../../../../../../common/components/TextInput';
import {
  PHONE_MATCH_REGEX,
  URL_REGEX,
} from '../../../../../../../common/constants/validations';
import './ChatWidgetGreetingButtonEditor.pcss';

type ChatWidgetGreetingButtonEditorProps = {
  buttons: PromoterCustomButton[];
  onChangeButtons: (buttons: PromoterCustomButton[]) => void;
};

const PromoterCustomButtonTypeNames: Record<
  PromoterCustomButton['type'],
  { icon: JSX.Element; name: string }
> = {
  text: {
    name: 'Text',
    icon: (
      <i className="chat-widget-greeting-button-editor__type-icon fas fa-font" />
    ),
  },
  phone_number: {
    name: 'Phone',
    icon: (
      <i className="chat-widget-greeting-button-editor__type-icon fas fa-phone" />
    ),
  },
  direct_url: {
    name: 'Link',
    icon: (
      <i className="chat-widget-greeting-button-editor__type-icon fas fa-external-link-alt" />
    ),
  },
};

export const ChatWidgetGreetingButtonEditor = (
  props: ChatWidgetGreetingButtonEditorProps,
) => {
  const { buttons, onChangeButtons } = props;

  const [editIndex, setEditIndex] = useState<number | undefined>();
  const [editButton, setEditButton] = useState<PromoterCustomButton>();

  useEffect(
    () =>
      setEditButton(editIndex === undefined ? undefined : buttons[editIndex]),
    [buttons, editIndex],
  );

  const saveEditButton = () => {
    if (editIndex === undefined || !editButton) {
      return;
    }
    const updatedButtons = [...buttons];
    updatedButtons[editIndex] = editButton;
    onChangeButtons(updatedButtons);
    setEditIndex(undefined);
  };
  const deleteButton = (index: number) => {
    const updatedButtons = [...buttons];
    updatedButtons.splice(index, 1);
    onChangeButtons(updatedButtons);
    setEditIndex(undefined);
  };
  const addNewButton = () => {
    setEditIndex(buttons.length ?? 0);
    onChangeButtons([
      ...buttons,
      { type: 'text', title: 'New Button' } as PromoterCustomButton,
    ]);
  };

  const reorderButtons = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const updatedButtons = [...buttons];
    updatedButtons.splice(
      result.destination.index,
      0,
      updatedButtons.splice(result.source.index, 1)[0],
    );
    onChangeButtons(updatedButtons);
  };

  return (
    <>
      <MMdndDroppable
        droppableId="chat-widget-greeting-button-editor"
        type="chat-widget-greeting-button-editor"
        onDragEnd={reorderButtons}
      >
        {provided => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {buttons.map((b, i) => (
              <Draggable draggableId={String(i)} index={i}>
                {draggableProvided => (
                  <div
                    key={b.title}
                    className="chat-widget-greeting-button-editor__button"
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                  >
                    <div
                      {...draggableProvided.dragHandleProps}
                      style={{ height: 16 }}
                    >
                      <DragIcon color="#fff" />
                    </div>
                    <div
                      className="chat-widget-greeting-button-editor__button-title"
                      onClick={() => setEditIndex(i)}
                    >
                      {b.title}
                    </div>

                    <Popover
                      isOpen={editIndex === i}
                      containerStyle={{ zIndex: '1000003' }}
                      content={
                        <EditArea
                          editButton={editButton}
                          setEditButton={setEditButton}
                          saveEditButton={saveEditButton}
                          onDelete={() => deleteButton(i)}
                          onEndEditing={() => setEditIndex(undefined)}
                        />
                      }
                      position="right"
                    >
                      <TrashIcon
                        color="#fff"
                        style={{ cursor: 'pointer' }}
                        onClick={() => deleteButton(i)}
                      />
                    </Popover>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </MMdndDroppable>
      <div
        className="chat-widget-greeting-button-editor__add-button-link"
        onClick={addNewButton}
      >
        <PlusCircleIcon
          color="#3a50f3"
          className="chat-widget-greeting-button-editor__add-button-link-icon"
        />
        Add Button
      </div>
    </>
  );
};

type EditAreaProps = {
  editButton?: PromoterCustomButton;
  setEditButton: (updatedButton?: PromoterCustomButton) => void;
  onDelete: () => void;
  saveEditButton: () => void;
  onEndEditing: () => void;
};
const EditArea = ({
  editButton,
  setEditButton,
  onDelete,
  saveEditButton,
  onEndEditing,
}: EditAreaProps) => {
  const titleInputRef = useRef<
    HTMLTextAreaElement | HTMLInputElement | undefined
  >();
  const { disableMainScroll, enableMainScroll } = useViewActions();

  useEffect(() => {
    // Focus on mount
    // 250ms to account for popover time
    setTimeout(() => titleInputRef.current?.focus(), 250);

    // Disable main scroll on mount
    disableMainScroll();
    return () => {
      enableMainScroll();
    };
  }, [disableMainScroll, enableMainScroll]);

  const editButtonValid = useMemo(() => {
    if (!editButton?.title.length) {
      return false;
    }
    switch (editButton.type) {
      case 'phone_number':
        return !!editButton.payload?.match(PHONE_MATCH_REGEX);
      case 'direct_url':
        return !!editButton.url?.match(URL_REGEX);
      case 'text':
        return true;
      default:
        return false;
    }
  }, [editButton]);

  const userCountry = useSelector((store: StoreState) => store.env.userCountry);

  if (!editButton) {
    return <></>;
  }

  return (
    <OnClickElsewhere handleClick={onEndEditing}>
      <div className="chat-widget-greeting-button-editor__popup">
        <Heading size={4} isBold={false} text="Button Text" />
        <InputWithEmoji
          value={editButton?.title}
          onChange={t => setEditButton({ ...editButton, title: t })}
          inputRef={titleInputRef}
        />
        <div className="chat-widget-greeting-button-editor__types">
          {typedObjectKeys(PromoterCustomButtonTypeNames).map(type => (
            <TextLink
              key={type}
              className={classNames(
                'chat-widget-greeting-button-editor__type',
                {
                  'chat-widget-greeting-button-editor__type--selected':
                    editButton.type === type,
                },
              )}
              onClick={() => setEditButton({ ...editButton, type })}
              icon={PromoterCustomButtonTypeNames[type].icon}
            >
              {PromoterCustomButtonTypeNames[type].name}
            </TextLink>
          ))}
        </div>
        {editButton.type === 'phone_number' && (
          <PhoneInput
            className="chat-widget-greeting-button-editor__phone-input"
            value={editButton.payload ?? ''}
            country={userCountry}
            onChange={payload => setEditButton({ ...editButton, payload })}
          />
        )}
        {editButton.type === 'direct_url' && (
          <TextInput
            className="chat-widget-greeting-button-editor__url-input"
            placeholder="Insert URL here"
            value={editButton.url}
            onChange={e =>
              setEditButton({ ...editButton, url: e.target.value })
            }
          />
        )}
        <div className="chat-widget-greeting-button-editor__button-row">
          <DeleteButton
            className="chat-widget-greeting-button-editor__delete-button"
            css={{
              leftIcon:
                'chat-widget-greeting-button-editor__delete-button-icon',
            }}
            onClick={onDelete}
          />
          <div style={{ flexGrow: 1 }} />
          <PrimaryButton
            className="chat-widget-greeting-button-editor__button-row-button"
            color={ButtonColor.transparent}
            onClick={onEndEditing}
          >
            Cancel
          </PrimaryButton>
          <PrimaryButton
            className="chat-widget-greeting-button-editor__button-row-button"
            onClick={saveEditButton}
            disabled={!editButtonValid}
          >
            Save
          </PrimaryButton>
        </div>
      </div>
    </OnClickElsewhere>
  );
};
